<script lang="ts" setup>
    import { computed, reactive, ref, watch } from 'vue'
    import { useI18n } from 'vue-i18n'
    import type { UIChatMessage } from 'models'
    import PkStreamingMarkdown from './PkStreamingMarkdown.vue'
    import PkStreamingMarkdownAutoscroll from './PkStreamingMarkdownAutoscroll.vue'
    import PkChatbotStepToolDetail from './PkChatbotStepToolDetail.vue'
    import PkChatbotStepPayload from './PkChatbotStepPayload.vue'
    import {
        isReasoningPart,
        isToolPart,
        getPartIcon,
        getPartState,
        getToolPartLabel,
        getToolPartSummary,
        hasRichToolDetail,
        hasPayload,
        formatDuration as formatDurationWith,
    } from './utils'

    const props = defineProps<{
        message: UIChatMessage
        isStreaming?: boolean
        isDark?: boolean
    }>()

    const { t: $t, n: $n } = useI18n({
        useScope: 'global',
    })

    type MessagePart = UIChatMessage['parts'][number]

    interface Step {
        key: string
        part: MessagePart
        icon?: string
        label?: string
        summary?: string
        durationMs?: number
        isActive: boolean
        text?: string
        toolInput?: unknown
        toolOutput?: unknown
    }

    const steps = computed<Step[]>(() => {
        const parts = props.message.parts
        const partTimings = props.message.metadata?.partTimings
        const result: Step[] = []
        for (const [index, part] of parts.entries()) {
            const isReasoning = isReasoningPart(part)
            const isTool = isToolPart(part)
            if (!isReasoning && !isTool) {
                continue
            }
            const toolCallId =
                'toolCallId' in part ? (part.toolCallId as string) : undefined
            result.push({
                key: toolCallId ?? `${part.type}-${index}`,
                part,
                icon: getPartIcon(part),
                label: getToolPartLabel($t, part),
                summary: getToolPartSummary(part),
                durationMs: toolCallId ? partTimings?.[toolCallId] : undefined,
                isActive:
                    Boolean(props.isStreaming) && index === parts.length - 1,
                text: isReasoning && part.text.trim() ? part.text : undefined,
                toolInput: isTool && 'input' in part ? part.input : undefined,
                toolOutput:
                    isTool && 'output' in part ? part.output : undefined,
            })
        }
        return result
    })

    const totalDurationMs = computed(() => {
        const { createdAt, completedAt } = props.message.metadata ?? {}
        if (!createdAt || !completedAt) {
            return undefined
        }
        return completedAt - createdAt
    })

    const formatDuration = (ms?: number) => formatDurationWith($n, ms)

    const getStepError = (step: Step) =>
        getPartState(step.part) === 'output-error' &&
        'errorText' in step.part &&
        typeof step.part.errorText === 'string'
            ? step.part.errorText
            : undefined

    const hasBody = (step: Step) => {
        if (step.text || getStepError(step)) {
            return true
        }
        if (hasRichToolDetail(step.part)) {
            return step.toolInput !== undefined || step.toolOutput !== undefined
        }
        return hasPayload(step.toolInput) || hasPayload(step.toolOutput)
    }

    // --- Expand / collapse ---
    // Every step starts closed, the running one included: the timeline says
    // what the answer is doing, the body is opened only when asked for. The
    // reasoning of the active step still follows the stream once opened.
    const manualSteps = reactive(new Map<string, boolean>())
    const isStepExpanded = (step: Step) => manualSteps.get(step.key) ?? false
    const toggleStep = (step: Step) => {
        manualSteps.set(step.key, !isStepExpanded(step))
    }

    // The group is open while streaming and collapses to its header once the
    // answer is complete, unless the user toggled it manually.
    const manualGroup = ref<boolean | undefined>(undefined)
    const isGroupExpanded = computed(
        () => manualGroup.value ?? Boolean(props.isStreaming),
    )
    // Reset the manual override when a new stream starts so the timeline
    // opens again on regenerate.
    watch(
        () => props.isStreaming,
        (streaming) => {
            if (streaming) {
                manualGroup.value = undefined
                manualSteps.clear()
            }
        },
    )
</script>

<template>
    <div v-if="steps.length" class="pk-chatbot-steps">
        <button
            type="button"
            class="pk-chatbot-steps__header"
            :aria-expanded="isGroupExpanded"
            @click="manualGroup = !isGroupExpanded">
            <VvIcon
                v-if="isStreaming"
                name="line-md:loading-loop"
                class="shrink-0" />
            <VvIcon v-else name="ri:route-line" class="shrink-0" />
            <span class="pk-chatbot-steps__title">
                {{ $t('messagePart.steps') }}
            </span>
            <span class="pk-chatbot-steps__meta">
                {{ $t('messagePart.stepCount', steps.length) }}
                <template v-if="formatDuration(totalDurationMs)">
                    · {{ formatDuration(totalDurationMs) }}
                </template>
            </span>
            <VvIcon
                :name="
                    isGroupExpanded
                        ? 'ri:arrow-up-s-line'
                        : 'ri:arrow-down-s-line'
                "
                class="pk-chatbot-steps__chevron shrink-0 ml-auto" />
        </button>
        <ol v-if="isGroupExpanded" class="pk-chatbot-steps__list">
            <li
                v-for="step in steps"
                :key="step.key"
                class="pk-chatbot-steps__step"
                :class="{
                    'pk-chatbot-steps__step--active': step.isActive,
                }">
                <component
                    :is="hasBody(step) ? 'button' : 'div'"
                    :type="hasBody(step) ? 'button' : undefined"
                    class="pk-chatbot-steps__step-header"
                    :aria-expanded="
                        hasBody(step) ? isStepExpanded(step) : undefined
                    "
                    @click="hasBody(step) ? toggleStep(step) : undefined">
                    <span
                        class="pk-chatbot-steps-marker"
                        :class="{
                            'pk-chatbot-steps-marker--active': step.isActive,
                        }">
                        <VvIcon
                            v-if="step.isActive"
                            name="line-md:loading-loop" />
                        <VvIcon v-else-if="step.icon" :name="step.icon" />
                    </span>
                    <span class="pk-chatbot-steps__step-label">
                        {{ step.label }}
                    </span>
                    <span class="pk-chatbot-steps__step-summary">
                        {{ step.summary }}
                    </span>
                    <span
                        v-if="formatDuration(step.durationMs)"
                        class="pk-chatbot-steps__step-duration">
                        {{ formatDuration(step.durationMs) }}
                    </span>
                    <VvIcon
                        v-if="hasBody(step)"
                        :name="
                            isStepExpanded(step)
                                ? 'ri:arrow-up-s-line'
                                : 'ri:arrow-down-s-line'
                        "
                        class="pk-chatbot-steps__chevron shrink-0 ml-auto" />
                </component>
                <div
                    v-if="hasBody(step) && isStepExpanded(step)"
                    class="pk-chatbot-steps__step-body">
                    <PkStreamingMarkdownAutoscroll
                        v-if="step.text && step.isActive"
                        :markdown="step.text"
                        :is-dark
                        inner-class="wysiwyg"
                        class="pk-chatbot-steps__step-content pk-chatbot-steps__step-content--scroll" />
                    <div
                        v-else-if="step.text"
                        class="pk-chatbot-steps__step-content pk-chatbot-steps__step-content--scroll light-scrollbar">
                        <PkStreamingMarkdown
                            :markdown="step.text"
                            :is-dark
                            class="wysiwyg" />
                    </div>
                    <div
                        v-else-if="getStepError(step)"
                        class="pk-chatbot-steps__step-payload pk-chatbot-steps__step-payload--error">
                        <span class="pk-chatbot-steps__step-payload-label">
                            {{ $t('messagePart.stepError') }}
                        </span>
                        <pre>{{ getStepError(step) }}</pre>
                    </div>
                    <PkChatbotStepToolDetail
                        v-else-if="hasRichToolDetail(step.part)"
                        :part="step.part" />
                    <template v-else>
                        <PkChatbotStepPayload
                            v-if="hasPayload(step.toolInput)"
                            :label="$t('messagePart.stepInput')"
                            :value="step.toolInput" />
                        <PkChatbotStepPayload
                            v-if="hasPayload(step.toolOutput)"
                            :label="$t('messagePart.stepOutput')"
                            :value="step.toolOutput" />
                    </template>
                </div>
            </li>
        </ol>
    </div>
</template>

<style lang="scss">
    .pk-chatbot-steps {
        width: 100%;
        border: var(--spacing-px) solid var(--color-surface-3);
        border-radius: var(--rounded-lg);
        background-color: var(--color-surface-1);
        font-size: var(--text-12);
        color: var(--color-word-3);
        overflow: hidden;

        &__header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: 100%;
            min-height: var(--spacing-40);
            padding: var(--spacing-8) var(--spacing-sm);
            text-align: left;
            cursor: pointer;
            transition: background-color 0.2s ease;

            &:hover {
                background-color: var(--color-surface-2);
            }
        }

        &__chevron {
            font-size: var(--text-16);
        }

        &__title {
            font-weight: 600;
            color: var(--color-word-2);
            flex-grow: 1;
        }

        &__meta {
            color: var(--color-word-4);
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
        }

        &__list {
            background-color: var(--color-surface);
            border-top: var(--spacing-px) solid var(--color-surface-3);
            padding: var(--spacing-8) var(--spacing-sm);
            display: flex;
            flex-direction: column;
        }

        &__step {
            position: relative;

            // Vertical connector between step markers
            &:not(:last-child)::before {
                content: '';
                position: absolute;
                left: var(--spacing-10);
                top: var(--spacing-28);
                bottom: calc(var(--spacing-8) * -1);
                width: var(--spacing-px);
                background-color: var(--color-surface-4);
            }
        }

        &__step-header {
            display: flex;
            align-items: center;
            gap: var(--spacing-8);
            width: calc(100% + var(--spacing-16));
            min-height: var(--spacing-36);
            margin-inline: calc(var(--spacing-8) * -1);
            padding: var(--spacing-4) var(--spacing-8);
            text-align: left;

            &:is(button) {
                cursor: pointer;
                border-radius: var(--rounded);
                transition: background-color 0.2s ease;

                &:hover {
                    background-color: var(--color-surface-2);
                }
            }
        }

        &-marker {
            display: inline-flex;
            align-items: center;
            justify-content: center;
            width: var(--spacing-20);
            height: var(--spacing-20);
            flex-shrink: 0;
            position: relative;
            z-index: 1;
            background-color: var(--color-surface-1);
            border-radius: var(--rounded-full);
            border: var(--spacing-px) solid var(--color-surface-4);

            &--active {
                background-color: var(
                    --chatbot-main-color,
                    var(--color-surface-4)
                );
                border-color: transparent;
                color: var(--chatbot-contrast-color, var(--color-word-1));
            }
        }

        &__step-label {
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color-word-2);
        }

        &__step-summary {
            flex: 1 1 0;
            min-width: 0;
            overflow: hidden;
            text-overflow: ellipsis;
            white-space: nowrap;
            color: var(--color-word-4);
        }

        &__step-duration {
            color: var(--color-word-4);
            font-variant-numeric: tabular-nums;
            white-space: nowrap;
        }

        &__step--active &__step-label {
            color: var(--color-word-1);
        }

        &__step-body {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-4);
            margin-left: var(--spacing-28);
            margin-block: var(--spacing-4) var(--spacing-8);
        }

        &__step-content {
            border: var(--spacing-px) solid var(--color-surface-4);
            border-radius: var(--rounded);
            padding: var(--spacing-8);
            background-color: var(--color-surface-1);

            // The finished reasoning keeps the cap the streaming one had:
            // `.wysiwyg` hides its own overflow, so the scroll belongs to the
            // wrapper, exactly as in the autoscroll variant.
            &--scroll {
                max-height: var(--spacing-256);
                overflow: auto;
            }
        }

        &__step-payload {
            display: flex;
            flex-direction: column;
            gap: var(--spacing-2);

            pre {
                border: var(--spacing-px) solid var(--color-surface-4);
                border-radius: var(--rounded);
                padding: var(--spacing-8);
                font-size: var(--text-10);
                max-height: var(--spacing-160);
                overflow: auto;
                white-space: pre-wrap;
                word-break: break-word;
                font-family: var(--font-mono);
                background-color: var(--color-surface-1);
            }
        }

        &__step-payload-label {
            font-weight: 600;
            color: var(--color-word-3);
            text-transform: uppercase;
            font-size: var(--text-10);
            letter-spacing: 0.05em;
        }

        &__step-payload--error {
            .pk-chatbot-steps__step-payload-label,
            pre {
                color: var(--color-danger);
            }

            pre {
                border-color: var(--color-danger-lighten-5);
            }
        }
    }
</style>
